import {
  displayRunName,
  displayScopeLabel,
  localizeMonthLabel,
} from "@/utils/payroll-names";
import { useLocale } from "@/hooks/useLocale";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { LayoutGrid, Rows3 } from "lucide-react";

import { GeneratePayrollCTA } from "@/components/v2/sections/payroll/generate-payroll-cta";
import { ROUTES } from "@/constants/routes";
import {
  PayrollRunCard,
  type PayrollRunStatus,
} from "@/components/v2/sections/payroll/payroll-run-card";
import { ViewAction } from "@/components/table/actions";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CurrencyFlag } from "@/components/common/CurrencyFlag";
import { payrollRunStatusToVariant } from "@/utils/payrollRunStatusVariant";
import { payrollRunPaymentBadge } from "@/utils/payrollRunPaymentLabel";
import { PermissionButton } from "@/components/ui/permission-button";
import { Table, type ColumnDef } from "@/components/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/utils/cn";
import { usePageTitle } from "@/hooks/usePageTitle";
import { usePermissions } from "@/hooks/usePermissions";
import { useSelector } from "react-redux";
import { selectUser } from "@/store/slices/user.slice";
import { withBranding, formatDateDmy } from "@/utils/helper";
import { PayrollHistoryTable } from "./_components/PayrollHistoryTable";
import {
  useListRunsQuery,
  useListPayrollHistoryQuery,
  useListPayrollBranchesQuery,
  type IPayrollRun,
} from "@/services/(company)/payrollRunsApi";
import { PayrollRunStatusEnum } from "@/utils/enums/payroll.enum";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

type PayrollTab = "active" | "history";
type ActiveView = "cards" | "table";

const formatNet = (v: number | string) => {
  const n = typeof v === "string" ? Number(v.replace(/,/g, "")) : v;
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const safePayDate = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : formatDateDmy(d);
};

/**
 * Payroll-run status labels. The API sends `statusLabel` in English, so it can't
 * be shown directly — map the enum instead and fall back to the raw value so an
 * unrecognised status still renders readably.
 */
const RUN_STATUS_KEYS: Record<string, string> = {
  GENERATING: "company:payrolls.generating",
  DRAFT: "common:status.draft",
  IN_REVIEW: "common:shared_ui.in_review",
  APPROVED: "common:status.approved",
  PROCESSING: "company:payrolls.processing",
  COMPLETED: "common:status.completed",
  CANCELLED: "common:status.cancelled",
  FAILED: "company:payrolls.failed",
};

const translateRunStatus = (
  status: string,
  statusLabel: string | undefined,
  t: TFunction,
) => {
  const key = RUN_STATUS_KEYS[status?.toUpperCase()];
  return key ? t(key) : statusLabel || status;
};

const buildStatusOptions = (t: TFunction) => [
  { value: "all", label: t("company:payrolls.all_statuses") },
  { value: PayrollRunStatusEnum.DRAFT, label: t("company:payrolls.draft") },
  {
    value: PayrollRunStatusEnum.IN_REVIEW,
    label: t("common:shared_ui.in_review"),
  },
  { value: PayrollRunStatusEnum.APPROVED, label: t("common:status.approved") },
  {
    value: PayrollRunStatusEnum.PROCESSING,
    label: t("company:payrolls.processing"),
  },
  {
    value: PayrollRunStatusEnum.COMPLETED,
    label: t("common:status.completed"),
  },
  { value: PayrollRunStatusEnum.FAILED, label: t("company:payrolls.failed") },
];

const API_STATUS_TO_CARD: Partial<
  Record<PayrollRunStatusEnum, PayrollRunStatus>
> = {
  [PayrollRunStatusEnum.GENERATING]: "generating",
  [PayrollRunStatusEnum.DRAFT]: "draft",
  [PayrollRunStatusEnum.IN_REVIEW]: "in-review",
  [PayrollRunStatusEnum.APPROVED]: "approved",
  [PayrollRunStatusEnum.PROCESSING]: "processing",
  [PayrollRunStatusEnum.COMPLETED]: "completed",
  [PayrollRunStatusEnum.CANCELLED]: "cancelled",
};

const runToTotals = (run: IPayrollRun) =>
  (run.totalsByCurrency ?? []).map((t) => ({
    code: t.currencyCode,
    symbol: t.currencySymbol,
    employeeCount: t.employeeCount,
    net: Number(t.net ?? 0),
  }));

// Stage 1 = Scope set (GENERATING) → 6 = Processed (COMPLETED).
// PROCESSING and COMPLETED count as separate stages for the progress bar.
const STATUS_TO_STAGE: Partial<Record<PayrollRunStatusEnum, number>> = {
  [PayrollRunStatusEnum.GENERATING]: 1,
  [PayrollRunStatusEnum.DRAFT]: 2,
  [PayrollRunStatusEnum.IN_REVIEW]: 3,
  [PayrollRunStatusEnum.APPROVED]: 4,
  [PayrollRunStatusEnum.PROCESSING]: 5,
  [PayrollRunStatusEnum.COMPLETED]: 6,
};

const runToProgress = (run: IPayrollRun): number => {
  const stage = STATUS_TO_STAGE[run.status] ?? 0;
  return Math.round((stage / 6) * 100);
};

const progressColorClass = (percent: number): string => {
  if (percent >= 100) return "bg-success";
  if (percent >= 67) return "bg-primary";
  if (percent >= 34) return "bg-warning";
  return "bg-danger";
};

const FilterSelect = ({
  value,
  onChange,
  options,
  placeholder,
  triggerClassName,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
  triggerClassName?: string;
}) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger
      title={options.find((o) => o.value === value)?.label ?? placeholder}
      className={cn("h-9 w-36 bg-white text-xs font-medium", triggerClassName)}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o.value} value={o.value}>
          {o.label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const ActiveRunsTable = ({
  runs,
  onView,
}: {
  runs: IPayrollRun[];
  onView: (id: number) => void;
}) => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate: localeDate } = useLocale();
  const columns: ColumnDef<IPayrollRun>[] = [
    {
      key: "name",
      label: t("company:payrolls.run"),
      className: "w-[22%]",
      render: (r) => (
        <span className="text-sm font-semibold text-gray-900 truncate">
          {r.name
            ? displayRunName(t, r.name, localeDate)
            : (r.branchName ?? "—")}
        </span>
      ),
    },
    {
      key: "branch",
      label: t("company:payrolls.branch_departments"),
      className: "w-[14%]",
      render: (r) => (
        <div className="flex flex-col min-w-0">
          <span className="text-sm text-gray-900 truncate">
            {r.branchName ?? "—"}
          </span>
          {r.scopeLabel && r.scopeLabel !== r.branchName && (
            <span className="text-[11px] text-gray-400 truncate">
              {displayScopeLabel(t, r.scopeLabel)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "period",
      label: t("company:payrolls.period"),
      className: "w-[12%]",
      render: (r) => (
        <span className="text-sm text-gray-700 truncate">
          {r.periodLabel
            ? localizeMonthLabel(r.periodLabel, localeDate)
            : r.periodStart
              ? // eslint-disable-next-line i18next/no-literal-string -- date mask
                localeDate(new Date(r.periodStart), "MMM yyyy")
              : "—"}
        </span>
      ),
    },
    {
      key: "currencies",
      label: t("company:payrolls.currencies"),
      className: "w-[10%]",
      render: (r) =>
        r.currencies?.length ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {r.currencies.map((c) => (
              <CurrencyFlag
                key={c}
                code={c}
                className="text-xs text-gray-700"
              />
            ))}
          </div>
        ) : (
          <span className="text-gray-300">—</span>
        ),
    },
    {
      key: "employees",
      label: t("company:payrolls.employees"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{r.totalEmployees ?? 0}</span>
      ),
    },
    {
      key: "net",
      label: t("company:payrolls.net_pay"),
      className: "w-[14%]",
      render: (r) =>
        !r.totalsByCurrency?.length ? (
          <span className="text-gray-300">—</span>
        ) : (
          <div className="flex flex-col">
            {r.totalsByCurrency.map((t) => (
              <span
                key={t.currencyCode}
                className="text-xs text-gray-700 truncate"
              >
                {t.currencyCode} {formatNet(t.net)}
              </span>
            ))}
          </div>
        ),
    },
    {
      key: "progress",
      label: t("common:shared_ui.progress"),
      className: "w-[12%]",
      render: (r) => {
        const percent = runToProgress(r);
        return (
          <div className="flex flex-col gap-1 min-w-24">
            <span className="text-[11px] font-semibold text-gray-700 text-end">
              {percent}%
            </span>
            <Progress
              value={percent}
              className="h-1.5"
              indicatorClassName={progressColorClass(percent)}
            />
          </div>
        );
      },
    },
    {
      key: "payDate",
      label: t("company:payrolls.pay_date"),
      className: "w-[10%]",
      render: (r) => (
        <span className="text-sm text-gray-700">{safePayDate(r.payDate)}</span>
      ),
    },
    {
      key: "statusAction",
      label: t("company:payrolls.status"),
      className:
        "min-w-[180px] sticky end-0 z-10 bg-white shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)]",
      render: (r) => (
        <div className="flex items-center justify-between gap-3">
          <Badge
            variant={
              payrollRunPaymentBadge(r, t)?.variant ??
              payrollRunStatusToVariant(r.status)
            }
          >
            {payrollRunPaymentBadge(r, t)?.label ??
              translateRunStatus(r.status, r.statusLabel, t)}
          </Badge>
          <ViewAction
            tooltip={t("company:payrolls.view")}
            onClick={() => onView(r.id)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="rounded-xs border border-border bg-white flex flex-col gap-4 p-4">
      <Table<IPayrollRun>
        noPadding
        columns={columns}
        data={runs}
        onRowClick={(r) => onView(r.id)}
      />
    </div>
  );
};

export const CompanyPayrolls = () => {
  const { t } = useTranslation(["company", "common"]);
  const { formatDate: localeDate } = useLocale();
  usePageTitle(withBranding(t("company:payrolls.payrolls")));
  const navigate = useNavigate();
  const { can } = usePermissions();

  const currentUser = useSelector(selectUser);
  const isCompanyLevel = currentUser?.user?.isCompanySuperAdmin ?? false;
  const userBranchId = currentUser?.user?.branchId ?? null;

  const [tab, setTab] = useState<PayrollTab>("active");
  const [activeView, setActiveView] = useState<ActiveView>("table");
  // Start on the caller's own branch so the filter never renders a value that
  // has no matching option (branch-level users have no "All Branches" entry).
  const [branchId, setBranchId] = useState<string>(() =>
    userBranchId ? String(userBranchId) : "all",
  );
  const [status, setStatus] = useState<string>("all");

  const { data: branchesResponse } = useListPayrollBranchesQuery();
  const branches = branchesResponse?.data;
  const branchOptions = useMemo(() => {
    const list = (branches ?? []).map((b) => ({
      value: String(b.id),
      label: b.name,
    }));
    // "All Branches" is pointless — and leaves the trigger blank — for someone
    // scoped to exactly one branch; anyone else needs it to clear the filter.
    const showAll = isCompanyLevel || list.length !== 1;
    return showAll
      ? [{ value: "all", label: t("common:ui_kit.all_branches") }, ...list]
      : list;
  }, [isCompanyLevel, branches, t]);

  // Once the scoped branch list arrives, settle on a value that actually exists
  // in it — the user's branch when it is selectable, otherwise the first option.
  // Runs once so a manual pick is never overwritten by a refetch.
  const branchDefaultApplied = useRef(false);
  useEffect(() => {
    if (branchDefaultApplied.current || !branches || !branchOptions.length) {
      return;
    }
    branchDefaultApplied.current = true;
    const preferred = userBranchId ? String(userBranchId) : null;
    if (preferred && branchOptions.some((o) => o.value === preferred)) {
      setBranchId(preferred);
      return;
    }
    setBranchId((current) =>
      branchOptions.some((o) => o.value === current)
        ? current
        : branchOptions[0].value,
    );
  }, [branches, branchOptions, userBranchId]);

  const { data: activeResponse, isLoading: isLoadingActive } = useListRunsQuery(
    {
      bucket: "ACTIVE",
      limit: 20,
      ...(branchId !== "all" ? { branchId: Number(branchId) } : {}),
      ...(status !== "all" ? { status: status as PayrollRunStatusEnum } : {}),
    },
  );

  /**
   * Both tab counters load with the page, whichever tab is open.
   *
   * The history count used to arrive only from inside the History table, so
   * on the Active tab it read "Payroll History (0)" until that tab was opened
   * — and the active query was skipped on History, the same problem the other
   * way round. These are the exact args `PayrollHistoryTable` sends for page
   * 1, so opening the tab reads this from the cache rather than asking twice.
   */
  const { data: historyResponse } = useListPayrollHistoryQuery({
    page: 1,
    limit: 10,
    ...(branchId !== "all" ? { branchId: Number(branchId) } : {}),
    ...(status !== "all" ? { status } : {}),
  });
  const historyCount = historyResponse?.meta?.tabCounts?.history ?? 0;

  const activeRuns = activeResponse?.data ?? [];
  const activeCount =
    activeResponse?.meta?.totalItems ??
    activeResponse?.meta?.total ??
    activeRuns.length;

  const tabs: { value: PayrollTab; label: string; count: number }[] = [
    {
      value: "active",
      label: t("company:payrolls.all_active_runs"),
      count: activeCount,
    },
    {
      value: "history",
      label: t("company:payrolls.payroll_history"),
      count: historyCount,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <GeneratePayrollCTA
        onGenerate={() => navigate(ROUTES.HOME.COMPANY.PAYROLL.GENERATE)}
        // Any tab: generating does not depend on which list is showing (B52).
        disabled={!can(PLATFORM_SECTION_ENUM.PAYROLL, "create")}
        disabledReason={t("company:payrolls.no_permission_generate_payroll")}
      />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-stretch border-b border-border">
          {tabs.map((t) => {
            const isActive = tab === t.value;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTab(t.value)}
                className={cn(
                  "relative -mb-px flex shrink-0 items-center gap-2 px-3 py-3 md:px-4 md:py-4 text-[10px] md:text-xs font-semibold uppercase tracking-wide transition-colors duration-200",
                  isActive
                    ? "text-primary bg-primary/10 border-b-[3px] border-primary"
                    : "text-primary-strong hover:bg-primary/5 hover:text-primary",
                )}
              >
                {t.label} ({t.count})
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {tab === "active" && (
            <TooltipProvider delayDuration={200}>
              <div className="flex items-center border border-input rounded-xs h-9 bg-white overflow-hidden">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setActiveView("table")}
                      aria-label={t("company:payrolls.table_view")}
                      aria-pressed={activeView === "table"}
                      className={cn(
                        "flex items-center justify-center w-9 h-9 transition-colors",
                        activeView === "table"
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <Rows3 size={14} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t("company:payrolls.table_view")}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => setActiveView("cards")}
                      aria-label={t("company:payrolls.card_view")}
                      aria-pressed={activeView === "cards"}
                      className={cn(
                        "flex items-center justify-center w-9 h-9 transition-colors border-s border-input",
                        activeView === "cards"
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground",
                      )}
                    >
                      <LayoutGrid size={14} />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {t("company:payrolls.card_view")}
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}
          <FilterSelect
            value={branchId}
            onChange={setBranchId}
            options={branchOptions}
            placeholder={t("common:ui_kit.branch")}
            triggerClassName="w-44"
          />
          <FilterSelect
            value={status}
            onChange={setStatus}
            options={buildStatusOptions(t)}
            placeholder={t("company:payrolls.status")}
          />
        </div>
      </div>

      {tab === "active" &&
        (isLoadingActive ? (
          <div className="text-sm text-gray-400 py-8 text-center">
            {t("company:payrolls.loading_runs")}
          </div>
        ) : activeRuns.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xs border border-dashed border-border bg-white py-12 px-6 text-center">
            <span className="text-sm font-semibold text-gray-700">
              {t("company:payrolls.no_active_payroll_runs")}
            </span>
            <span className="text-xs text-gray-400 max-w-sm">
              {t(
                "company:payrolls.start_a_new_payroll_run_to_calculate_review_and_process_",
              )}
            </span>
            <PermissionButton
              type="button"
              variant="primary"
              section={PLATFORM_SECTION_ENUM.PAYROLL}
              action="create"
              mode="hide"
              onClick={() => navigate(ROUTES.HOME.COMPANY.PAYROLL.GENERATE)}
              className="mt-1"
            >
              {t("company:payrolls.generate_payroll")}
            </PermissionButton>
          </div>
        ) : activeView === "cards" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
            {activeRuns.map((run) => {
              // eslint-disable-next-line i18next/no-literal-string -- card status enum value, not copy
              const cardStatus = API_STATUS_TO_CARD[run.status] ?? "in-review";
              const currencies = run.currencies?.length
                ? run.currencies.join(", ")
                : "—";
              const periodLabel = run.periodLabel
                ? localizeMonthLabel(run.periodLabel, localeDate)
                : run.periodStart
                  ? // eslint-disable-next-line i18next/no-literal-string -- date mask
                    localeDate(new Date(run.periodStart), "MMM yyyy")
                  : "—";
              const meta: string[] = [];
              if (run.scopeLabel)
                meta.push(displayScopeLabel(t, run.scopeLabel));
              if (run.branchName && run.branchName !== run.scopeLabel) {
                meta.push(run.branchName);
              }

              return (
                <PayrollRunCard
                  key={run.id}
                  name={
                    run.name
                      ? displayRunName(t, run.name, localeDate)
                      : (run.branchName ?? "—")
                  }
                  period={periodLabel}
                  meta={meta}
                  currencies={currencies}
                  employeesCount={run.totalEmployees}
                  totals={runToTotals(run)}
                  progressPercent={runToProgress(run)}
                  payDate={
                    run.payDate
                      ? format(new Date(run.payDate), "dd/MM/yyyy")
                      : "—"
                  }
                  status={cardStatus}
                  statusBadge={payrollRunPaymentBadge(run, t)}
                  primaryActionLabel={t("company:payrolls.view")}
                  onPrimaryAction={() =>
                    navigate(ROUTES.HOME.COMPANY.PAYROLL.RUN(run.id))
                  }
                />
              );
            })}
          </div>
        ) : (
          <ActiveRunsTable
            runs={activeRuns}
            onView={(id) => navigate(ROUTES.HOME.COMPANY.PAYROLL.RUN(id))}
          />
        ))}

      {tab === "history" && (
        <PayrollHistoryTable
          branchId={branchId !== "all" ? Number(branchId) : undefined}
          status={status !== "all" ? status : undefined}
          onRowView={(id) => navigate(ROUTES.HOME.COMPANY.PAYROLL.RUN(id))}
        />
      )}
    </div>
  );
};

export default CompanyPayrolls;
